import CloseIcon from "@mui/icons-material/Close";
import { Box, ButtonBase, CircularProgress, IconButton, Modal, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useHiringStaffedNurses } from "../hooks/useHiringStaffedNurses";
import type { HiringGridCoord } from "../types";
import { RADIUS, SLATE } from "../utils/styleTokens";
import StaffedNurseRow from "./StaffedNurseRow";

type Props = {
	open: boolean;
	onClose: () => void;
	coord: HiringGridCoord;
	jobId: string;
	jobDisplayName: string;
};

const ActualCellNursesModal: FC<Props> = ({ open, onClose, coord, jobId, jobDisplayName }) => {
	const { t } = useTranslation();
	const { data, isLoading, isError } = useHiringStaffedNurses({ coord, jobId, enabled: open });

	return (
		<Modal
			open={open}
			onClose={onClose}
			slotProps={{ backdrop: { sx: { bgcolor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" } } }}
			sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}
			data-testid="hiring-actual-nurses-dialog"
		>
			<Box
				sx={{
					width: "100%",
					maxWidth: 512,
					maxHeight: "90vh",
					display: "flex",
					flexDirection: "column",
					bgcolor: "white",
					borderRadius: RADIUS.lg,
					p: 3,
					boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
					outline: "none",
				}}
			>
				<Box sx={{ mb: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
					<Box>
						<Typography sx={{ fontSize: 20, fontWeight: 700, color: SLATE[900] }}>
							{t("hiring.actualNurses.title", { job: jobDisplayName, shift: coord.shiftLabel })}
						</Typography>
						<Typography sx={{ fontSize: 13, color: SLATE[500], mt: 0.25 }}>{t("hiring.actualNurses.subtitle")}</Typography>
					</Box>
					<IconButton onClick={onClose} sx={{ p: 0.5, borderRadius: "50%", color: SLATE[500], "&:hover": { bgcolor: SLATE[100] } }}>
						<CloseIcon sx={{ fontSize: 20 }} />
					</IconButton>
				</Box>

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 0.5,
						border: `1px solid ${SLATE[200]}`,
						borderRadius: RADIUS.md,
						p: 0.5,
						overflow: "auto",
						flex: 1,
						minHeight: 0,
					}}
				>
					{isLoading && (
						<Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
							<CircularProgress size={24} data-testid="hiring-actual-nurses-loading" />
						</Box>
					)}
					{isError && (
						<Box sx={{ p: 2 }}>
							<Typography color="error" data-testid="hiring-actual-nurses-error">
								{t("hiring.actualNurses.fetchError")}
							</Typography>
						</Box>
					)}
					{data && data.staff.length === 0 && (
						<Box sx={{ p: 2 }}>
							<Typography sx={{ color: SLATE[500] }} data-testid="hiring-actual-nurses-empty">
								{t("hiring.actualNurses.empty")}
							</Typography>
						</Box>
					)}
					{data && data.staff.length > 0 && data.staff.map((nurse, idx) => <StaffedNurseRow key={nurse.nurseId} nurse={nurse} index={idx} />)}
				</Box>

				<Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
					<ButtonBase
						onClick={onClose}
						sx={{
							px: 2,
							py: 1,
							borderRadius: RADIUS.md,
							color: SLATE[600],
							fontWeight: 500,
							fontSize: 13,
							"&:hover": { bgcolor: SLATE[100] },
						}}
					>
						{t("close")}
					</ButtonBase>
				</Box>
			</Box>
		</Modal>
	);
};

export default ActualCellNursesModal;
